// Top bar + main content wrapper for authenticated pages.
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Brand } from "./Brand";
import { useAuth } from "../lib/AuthContext";
import { APP_VERSION } from "../lib/version";
import { DebugPanel } from "./DebugPanel";
import { CookingChip } from "./CookingChip";
import clsx from "clsx";
import { useState, type ReactNode } from "react";

const ENV_LABEL = (() => {
  if (typeof window === "undefined") return "";
  if (window.location.hostname.startsWith("stage.")) return "Stage";
  if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") return "Dev";
  return "";
})();

function UserMenu({ initials, email, onSignOut }: { initials: string; email: string | null; onSignOut: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((x) => !x)}
        className="w-8 h-8 rounded-full bg-paper border border-hairline-bold text-ink-body font-serif text-sm flex items-center justify-center hover:border-terracotta hover:text-terracotta transition-colors"
        title={email ?? "Account"}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {initials}
      </button>
      {open ? (
        <div
          onMouseLeave={() => setOpen(false)}
          className="absolute right-0 mt-2 w-52 rounded-sm border border-hairline-bold bg-paper shadow-[0_12px_28px_-10px_rgba(45,28,20,0.2)] z-20"
          role="menu"
        >
          {email ? (
            <div className="px-4 py-3 border-b border-hairline">
              <div className="font-mono text-[10px] tracking-smallcaps uppercase text-ink-dim">Signed in as</div>
              <div className="font-body text-xs text-ink-body truncate" title={email}>{email}</div>
            </div>
          ) : null}
          <Link
            to="/account"
            onClick={() => setOpen(false)}
            className="block px-4 py-2.5 font-body text-sm text-ink-body hover:bg-parchment"
            role="menuitem"
          >
            Account &amp; billing
          </Link>
          <Link
            to="/pricing"
            onClick={() => setOpen(false)}
            className="block px-4 py-2.5 font-body text-sm text-ink-body hover:bg-parchment"
            role="menuitem"
          >
            Pricing
          </Link>
          <button
            onClick={() => { setOpen(false); onSignOut(); }}
            className="w-full text-left px-4 py-2.5 font-body text-sm text-wax hover:bg-parchment border-t border-hairline"
            role="menuitem"
          >
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}

function NavItem({ to, label, primary = false, stamp = false }: { to: string; label: string; primary?: boolean; stamp?: boolean }) {
  // Three nav-item registers:
  //
  //   - default → italic Cormorant + terracotta foil-rule when active
  //     (Library, etc.) — the everyday public surface.
  //   - primary → italic Cormorant + foil-gold ★ prefix, larger, no box
  //     (Dream Your Book) — the hero CTA. Star is intentional and
  //     preserved across the redesign.
  //   - stamp   → mono uppercase tracking-stamp inside a foil-bordered
  //     pill (DB Engine) — signals "developer surface", visually
  //     distinct from the two italic primary tabs without competing
  //     with them.
  //
  // The brand wordmark has its own ❦-flank treatment in the footer, so
  // brand · CTA · selected · stamp are all visually separable.
  if (stamp) {
    return (
      <NavLink
        to={to}
        end
        className={({ isActive }) =>
          clsx(
            "font-mono uppercase tracking-stamp text-[10.5px] leading-none rounded-sm border px-2 py-1 transition-colors duration-200 ease-observatory whitespace-nowrap",
            isActive
              ? "text-terracotta border-terracotta/60 bg-terracotta/5"
              : "text-ink-faint border-foil/40 hover:text-terracotta hover:border-terracotta/50",
          )
        }
      >
        DB · Engine
      </NavLink>
    );
  }
  if (primary) {
    return (
      <NavLink
        to={to}
        end
        className={({ isActive }) =>
          clsx(
            "relative font-serif italic leading-none transition-colors duration-200 ease-observatory",
            "text-[20px] sm:text-[21px] font-medium tracking-[-0.005em]",
            isActive ? "text-terracotta" : "text-terracotta hover:text-wax",
          )
        }
      >
        <span aria-hidden className="text-terracotta text-[0.78em] not-italic mr-1.5">★</span>
        <span>{label}</span>
      </NavLink>
    );
  }
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        clsx(
          "relative font-serif italic text-[19px] sm:text-[20px] leading-none transition-colors duration-200 ease-observatory",
          isActive
            ? "text-terracotta"
            : "text-ink-body/85 hover:text-terracotta",
        )
      }
    >
      {({ isActive }) => (
        <span className="inline-flex flex-col items-start">
          <span className="tracking-[-0.005em]">{label}</span>
          <span
            className={clsx(
              "mt-1.5 h-[1.5px] bg-terracotta transition-[width,opacity] duration-300 ease-observatory",
              isActive
                ? "w-full opacity-100"
                : "w-0 opacity-0 group-hover:w-full group-hover:opacity-80",
            )}
          />
        </span>
      )}
    </NavLink>
  );
}

/**
 * UtilityLink — right-side nav register for personal/account surfaces.
 * Tiny icon + label, ink-dim by default, terracotta on hover/active.
 * Same baseline as the user menu so the cluster reads as a single
 * utility group, not as primary navigation.
 */
function UtilityLink({ to, icon, label }: { to: string; icon: string; label: string }) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        clsx(
          "hidden sm:inline-flex items-center gap-1.5 font-serif text-[12.5px] leading-none transition-colors duration-200 ease-observatory whitespace-nowrap",
          isActive ? "text-terracotta" : "text-ink-dim hover:text-terracotta",
        )
      }
    >
      <span aria-hidden className="text-[14px] leading-none not-italic">{icon}</span>
      <span>{label}</span>
    </NavLink>
  );
}


export function Shell({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  const nav = useNavigate();
  const initials = (user?.name ?? user?.email ?? "?").slice(0, 1).toUpperCase();

  return (
    <div className="min-h-screen grid grid-rows-[auto_1fr_auto]">
      <header className="sticky top-0 z-40 bg-petal/95 backdrop-blur-sm flex items-center justify-between gap-3 px-4 sm:px-8 lg:px-12 py-4 sm:py-5 border-b border-hairline-bold">
        <div className="flex items-center gap-4 sm:gap-8 lg:gap-12 min-w-0">
          <Link to="/" className="shrink-0"><Brand /></Link>
          <nav className="flex items-baseline gap-4 sm:gap-6 lg:gap-8 overflow-x-auto no-scrollbar">
            {/* Left-aligned primary cluster — public surfaces only.
                My Books + Pricing moved to the right utility cluster. */}
            <NavItem to="/library" label="Library" />
            <NavItem to="/dreambook" label="Dream Your Book" primary />
            {/* Workshop re-enabled 2026-05-31: the jam-pattern redesign
                shipped + verified (Characters · Plots · Voices). */}
            <NavItem to="/workshop" label="Workshop" />
            <NavItem to="/db-engine" label="DB Engine" stamp />
          </nav>
        </div>
        <div className="flex items-center gap-3 sm:gap-4 lg:gap-5 text-[13px] text-ink-dim shrink-0">
          {/* Utility cluster — personal/account surfaces. Tiny icon +
              label pairs (style C) so they read as utility, not primary. */}
          <UtilityLink to="/my-books" icon="📚" label="My Books" />
          <UtilityLink to="/pricing"  icon="$"  label="Pricing"  />
          {ENV_LABEL ? (
            <span className="hidden sm:inline-block px-2.5 py-1 border border-hairline-bold rounded-sm eyebrow">
              {ENV_LABEL}
            </span>
          ) : null}
          {/* Build version — bumped by 0.01 on every deploy so the user
              can confirm a hard-refresh actually picked up the new
              build. */}
          <span
            className="hidden md:inline-block font-mono text-[10px] tracking-smallcaps uppercase text-ink-dim"
            title="Frontend build version (bumps on every deploy)"
          >
            v{APP_VERSION}
          </span>
          {user?.role === "admin" ? (
            <span className="hidden sm:inline font-mono text-terracotta text-xs">admin</span>
          ) : null}
          <UserMenu initials={initials} email={user?.email ?? null} onSignOut={() => { signOut(); nav("/login"); }} />
        </div>
      </header>

      <main>{children}</main>

      {/* Stage debug widget — floating, bottom-left. Lets a tester copy a
          client-side snapshot (version + fetch feed + console errors)
          in one click. */}
      <DebugPanel />

      {/* App-level "still cooking" chip — bottom-right. Fed by the
          generationTracker so an in-flight AI generation stays visible
          across tab switches, scrolling, and route changes. */}
      <CookingChip />

      <footer className="relative px-4 sm:px-8 lg:px-12 pt-10 pb-7 border-t border-hairline-bold flex flex-col sm:flex-row items-center sm:items-center justify-between gap-4 sm:gap-2">
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-petal px-3 text-terracotta text-lg" aria-hidden>❦</span>
        {/* Colophon wordmark with the ceremonial tagline underneath — the
            brand appears in full only here, in the footer, where it reads
            as the book's imprint line rather than nav chrome. */}
        <span className="sm:order-1">
          <Brand size="sm" tagline />
        </span>
        <span className="font-body italic text-[13px] text-ink-dim hidden sm:inline">A quiet observatory where books are dreamed into being.</span>
        <span className="font-mono text-[11px] tracking-smallcaps uppercase text-ink-dim sm:order-3">© mmxxvi &middot; MerakiLabs</span>
      </footer>
    </div>
  );
}
